import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CardHeader from '@material-ui/core/CardHeader';
import { DefaultButton } from '../Buttons'
import BidSessionsList from '../BidSessionsList';

import { groupByGroupSequence } from '../../scenes/BidSession/helpers'

import { httpStatus } from '../../data/constants'
import { useLanguage } from '../../context/LanguageContext';

import './styles.scss'

const OkButton = ({ className = '', ...props }) => (
  <DefaultButton
    className={`save-apply-next-btn ${className}`}
    {...props}
  />
);

const CancelButton = ({ className = '', ...props }) => (
  <DefaultButton
    className={`cancel-back-btn ${className}`}
    {...props}
  />
);

const ImportPreferencesDialog = ({
  openDialog = false,
  dialogMessage,
  dialogTitle,
  okButtonLabel = "Ok",
  handleOpen = () => { },
  staffId = '',
  currentBidSessionId = 0,
  categoryId = 0,
  optionPropertyId = 0,
  applicationType = '',
  fetchPreviousBidSession = () => { },
  handleImportPreviousBidPreferences = () => { },
  showNotification = () => { },
  loadingImportPreviousBids = false
}) => {
  const { labels } = useLanguage();
  const IMPORT_PREVIOUS_BID = labels.IMPORT_PREVIOUS_BID || {};
  const RESERVE_DOWN = labels.RESERVE_DOWN || {};

  const [bidSessions, setBidSessions] = useState([])
  const [selectedBidSessionId, setSelectedBidSessionId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingSmall, setLoadingSmall] = useState(false)
  const [bidPreferences, setBidPreferences] = useState([])
  const [selectedCombinedPreferencesId, setSelectedCombinedPreferencesId] = useState('')
  const [combinedGroups, setCombinedGroups] = useState([])
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  const [groupPreferences, setGroupPreferences] = useState([])
  const [reserveLineNames, setReserveLineNames] = useState('');


  // get the list of previous bid sessions
  useEffect(() => {
    if (openDialog) {
      setLoading(true)
      fetchPreviousBidSession(staffId, categoryId, optionPropertyId, applicationType, currentBidSessionId).then((response) => {
        if (response.status !== httpStatus.OK) {
          setLoading(false)
          showNotification(response.data.message, 'error')
        } else {
          setLoading(false)
          setBidSessions(response.data)
        }
      })
    }
    if (!openDialog) {
      setBidSessions([])
      setSelectedBidSessionId(null)
      setSelectedCombinedPreferencesId('')
      setBidPreferences([])
      setGroupPreferences([])
      setCombinedGroups([])
      setSelectedGroupId(null)
    }
  }, [openDialog])

  // get the list of bid preferences from the selected bid session
  useEffect(() => {
    setLoadingSmall(true)
    const bidSession = bidSessions.find(session => session.bidSessionId === selectedBidSessionId)
    if (bidSession && bidSession.importable) {
      setLoadingSmall(false)
      setBidPreferences(bidSession.bidDtos)
    }
  }, [selectedBidSessionId, currentBidSessionId])

  // group the bid preferences by groupSequence
  useEffect(() => {
    if (selectedCombinedPreferencesId) {
      setCombinedGroups(groupByGroupSequence(bidPreferences.filter(pref => pref.groupName === selectedCombinedPreferencesId)))
    }
  }, [selectedCombinedPreferencesId])

  // get the group preferences
  useEffect(() => {
    if (selectedGroupId) {
      const group = combinedGroups.find(group => group.groupSequence === selectedGroupId)
      setGroupPreferences(group.preferences)
    }
  }, [selectedGroupId])

  useEffect(() => {
    setLoadingSmall(true)
    const bidSession = bidSessions.find(session => session.bidSessionId === selectedBidSessionId)
    if (bidSession && bidSession.importable) {
      setLoadingSmall(false)
      setBidPreferences(bidSession.bidDtos)
      const rl = (bidSession.reserveLineDto || [])
        .map(line => line.name)
        .filter(Boolean)
        .join(', ')
      setReserveLineNames(rl)
    } else {
      setReserveLineNames('')
    }
  }, [selectedBidSessionId, currentBidSessionId])

  const selectBidsession = (id) => {
    if (selectedBidSessionId === id) {
      setSelectedBidSessionId(null)
      setSelectedCombinedPreferencesId('')
      setBidPreferences([])
      setGroupPreferences([])
      setCombinedGroups([])
      setSelectedGroupId(null)
    }
    else setSelectedBidSessionId(id)
  }

  const selectCombined = (id) => {
    if (selectedCombinedPreferencesId === id) {
      setSelectedCombinedPreferencesId('')
      setGroupPreferences([])
      setCombinedGroups([])
      setSelectedGroupId(null)
    }
    else {
      setSelectedCombinedPreferencesId(id)
      setSelectedGroupId(null)
    }
  }

  const selectGroup = (id) => {
    if (selectedGroupId === id) {
      setSelectedGroupId(null)
      setGroupPreferences([])
    }
    else setSelectedGroupId(id)
  }



  return (
    <Dialog
      open={openDialog}
      onClose={handleOpen}
      aria-labelledby="import-preferences-dialog-title"
      aria-describedby="import-preferences-dialog-description"
      maxWidth={'lg'}
      fullWidth
      scroll="paper"
    >
      <DialogTitle id="import-preferences-dialog-title" className="dialogTitle">
        <CardHeader title={dialogTitle} subtitle={dialogMessage} className='bid-adding-title' />
      </DialogTitle>
      <DialogContent>
        <BidSessionsList
          loading={loading}
          bidSessions={bidSessions}
          selectedBidSessionId={selectedBidSessionId}
          bidPreferences={bidPreferences}
          combinedGroups={combinedGroups}
          selectedCombinedPreferencesId={selectedCombinedPreferencesId}
          selectedGroupId={selectedGroupId}
          groupPreferences={groupPreferences}
          reserveLineNames={reserveLineNames}
          loadingSmall={loadingSmall}
          loadingImportPreviousBids={loadingImportPreviousBids}
          onSelectBidSession={selectBidsession}
          onSelectCombined={selectCombined}
          onSelectGroup={selectGroup}
        />
      </DialogContent>

      <DialogActions className="dialogActions-flex-end">
        <CancelButton onClick={handleOpen} color="primary">
          {IMPORT_PREVIOUS_BID.CANCEL}
        </CancelButton>
        <OkButton
          onClick={() => handleImportPreviousBidPreferences(staffId, currentBidSessionId, selectedBidSessionId, bidPreferences)}
          disabled={selectedBidSessionId === null || (() => {
            const selectedSession = bidSessions.find(session => session.bidSessionId === selectedBidSessionId);
            const hasImportableOtherOptions = selectedSession && selectedSession.otherOptionDtos &&
              selectedSession.otherOptionDtos.length > 0 &&
              selectedSession.otherOptionDtos.some(option => option.importable);
            return (bidPreferences.length === 0 && !reserveLineNames && !hasImportableOtherOptions);
          })()}
          color="primary"
        >
          {IMPORT_PREVIOUS_BID.IMPORT}
        </OkButton>
      </DialogActions>
    </Dialog>
  )
}

ImportPreferencesDialog.propTypes = {
  openDialog: PropTypes.bool.isRequired,
  dialogMessage: PropTypes.string.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  handleOpen: PropTypes.func.isRequired,
  staffId: PropTypes.string,
  currentBidSessionId: PropTypes.number,
  fetchPreviousBidSession: PropTypes.func.isRequired,
  handleImportPreviousBidPreferences: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  loadingImportPreviousBids: PropTypes.bool.isRequired,
  optionPropertyId: PropTypes.number,
  applicationType: PropTypes.string.isRequired
}

export default ImportPreferencesDialog
